import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import {
  updateGetFetchTest,
  updateTestsApi,
  deleteTestById,
} from "../pages/services/apiService";
import {
  fetchAllSubjects,
  fetchModulesBySubject,
  fetchTopicsByModule,
} from "../pages/services/noteHierarchyService";
import { fetchSections } from "../pages/services/notesService";
import { toDateTimeLocal, fromDateTimeLocal } from "./utils/utility";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";
import "../styles/AddForm.css";
import "../styles/AddTests.css";
import "../styles/UpdateTest.css";

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const SelectField = ({ label, required, error, name, refProp, value, options, placeholder, onChange, disabled }) => (
  <Field label={label} required={required} error={error}>
    <select name={name} ref={refProp} value={value} onChange={onChange} disabled={disabled}
      className={error ? "error-input" : ""}>
      <option value="">{placeholder}</option>
      {options.map((o, i) =>
        typeof o === "string"
          ? <option key={i} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  </Field>
);

const UpdateTest = () => {
  const testTitleRef         = useRef(null);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const testTopicRef         = useRef(null);
  const testDurationRef      = useRef(null);
  const testStartFromRef     = useRef(null);
  const testStartToRef       = useRef(null);
  const testEndTimeRef       = useRef(null);
  const testSectionRef       = useRef(null);
  const analysisStartFromRef = useRef(null);
  const analysisStartToRef   = useRef(null);
  const analysisEndTimeRef   = useRef(null);
  const analysisDurationRef  = useRef(null);
  const isAnalysisRequiredRef = useRef(null);
  const protectiveTestRef    = useRef(null);
  const isFullScreenRef      = useRef(null);
  const testTypeRef          = useRef(null);
  const requiredTimerRef     = useRef(null);
  const testStatusRef        = useRef(null);

  const { id: testId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    testTitle: "", testDuration: "", testDescription: "",
    testInstructions: "", testSubject: "", testTopic: "", testStartFrom: "",
    testStartTo: "", testEndTime: "", testModule: "", testSection: "",
    analysisStartFrom: "", analysisStartTo: "", analysisEndTime: "",
    analysisDuration: "", isAnalysisRequired: "", protectiveTest: false,
    isFullScreen: false, testType: "", requiredTimer: "", testStatus: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showAnalysisFields, setShowAnalysisFields] = useState(false);

  const [subjects, setSubjects] = useState([]);
  const [modules, setModules]   = useState([]);
  const [topics, setTopics]     = useState([]);
  const [sections, setSections] = useState([]);
  const [batches, setBatches]   = useState([]);

  // Turn the stored batch ids into {label,value} once the batch list is in —
  // the ids arrive with the test, the labels only with the options.
  useEffect(() => {
    const ids = formData._batchIds;
    if (!Array.isArray(ids) || !ids.length || !batches.length) return;
    setSelectedBatches(
      ids.map((id) => batches.find((b) => String(b.value) === String(id))).filter(Boolean)
    );
  }, [formData._batchIds, batches]);

  // Subject and module are multi-select; each holds react-select {label,value}
  // objects. Topic and section stay single and are optional.
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedModules, setSelectedModules]   = useState([]);

  useEffect(() => {
    const fetchTest = async () => {
      setLoading(true);
      try {
        const [batchRes, subjectRes] = await Promise.all([
          fetchBatchOptions(),
          fetchAllSubjects(),
        ]);
        setBatches(batchRes);   // already { label, value } from the shared source
        setSubjects(subjectRes);

        const { res, response } = await updateGetFetchTest(testId);
        if (!res.ok || !response?.data) {
          navigate("/not-found", { replace: true });
          return;
        }

        const d = response.data;
        // testSubject/testModule come back as arrays of {_id, subject|module}.
        // A pre-multi-select record may still hold a single object, so both
        // shapes are normalised here.
        const asList = (v) => (Array.isArray(v) ? v : v && v._id ? [v] : []);
        const subjectList = asList(d.testSubject).map(x => ({ value: String(x._id), label: x.subject || x.title || "" }));
        const moduleList  = asList(d.testModule).map(x => ({ value: String(x._id), label: x.module  || x.title || "" }));
        const topicId   = d.testTopic?._id   || "";
        const sectionId = d.testSection?._id  || "";

        setFormData({
          testInstructions: d.testInstructions || "",
          testDescription:  d.testDescription  || "",
          testTitle:        d.testTitle        || "",
          // Prefill from the full assignment list, falling back to the single
          // primary batch for tests written before batchIds existed.
          _batchIds: Array.isArray(d.batchIds) && d.batchIds.length
            ? d.batchIds.map(String)
            : (d.batch?._id ? [String(d.batch._id)] : []),
          testTopic:        topicId,
          testStartFrom:    toDateTimeLocal(d.testStartFrom) || "",
          testStartTo:      toDateTimeLocal(d.testStartTo)   || "",
          testEndTime:      toDateTimeLocal(d.testEndTime)   || "",
          testDuration:     d.testDuration     || "",
          testSection:      sectionId,
          isAnalysisRequired: d.isAnalysisRequired ? "true" : "false",
          protectiveTest:   d.protectiveTest   || false,
          isFullScreen:     d.isFullScreen     || false,
          analysisStartFrom: toDateTimeLocal(d.analysisStartFrom) || "",
          analysisStartTo:   toDateTimeLocal(d.analysisStartTo)   || "",
          analysisEndTime:   toDateTimeLocal(d.analysisEndTime)   || "",
          analysisDuration:  d.analysisDuration  || "",
          testType:          d.testType          || "",
          testStatus:        d.testStatus        || "",
          requiredTimer:     d.requiredTimer ? "true" : "false",
        });
        setSelectedSubjects(subjectList);
        setSelectedModules(moduleList);
        setShowAnalysisFields(d.isAnalysisRequired === true);

        // Load the cascade for everything already selected, merging across all
        // chosen subjects/modules so the saved values stay visible in the lists.
        const mergeById = (lists) => {
          const out = []; const seen = new Set();
          lists.flat().forEach(x => { if (!seen.has(x.value)) { seen.add(x.value); out.push(x); } });
          return out;
        };
        if (subjectList.length) {
          const mods = mergeById(await Promise.all(subjectList.map(x => fetchModulesBySubject(x.value).catch(() => []))));
          setModules(mods);
          if (moduleList.length) {
            const tops = mergeById(await Promise.all(moduleList.map(x => fetchTopicsByModule(x.value).catch(() => []))));
            setTopics(tops);
            if (topicId) {
              const secs = await fetchSections(topicId);
              setSections((secs || []).map(s => ({ value: s._id, label: s.title })));
            }
          }
        }
      } catch (err) {
        console.error("Error loading test:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId, navigate]);

  const mergeById = (lists) => {
    const out = []; const seen = new Set();
    lists.flat().forEach(x => { if (!seen.has(x.value)) { seen.add(x.value); out.push(x); } });
    return out;
  };

  const handleSubjectsChange = async (vals) => {
    setSelectedSubjects(vals);
    setFormData(p => ({ ...p, testTopic: "", testSection: "" }));
    setTopics([]); setSections([]);
    if (!vals.length) { setModules([]); setSelectedModules([]); return; }
    const mods = mergeById(await Promise.all(vals.map(v => fetchModulesBySubject(v.value).catch(() => []))));
    setModules(mods);
    const stillValid = new Set(mods.map(m => m.value));
    setSelectedModules(prev => prev.filter(m => stillValid.has(m.value)));
  };

  const handleModulesChange = async (vals) => {
    setSelectedModules(vals);
    setFormData(p => ({ ...p, testTopic: "", testSection: "" }));
    setSections([]);
    if (!vals.length) { setTopics([]); return; }
    setTopics(mergeById(await Promise.all(vals.map(v => fetchTopicsByModule(v.value).catch(() => [])))));
  };

  const handleTopicChange = async (topicId) => {
    setFormData(p => ({ ...p, testTopic: topicId, testSection: "" }));
    setSections([]);
    if (topicId) {
      try {
        const secs = await fetchSections(topicId);
        setSections((secs || []).map(s => ({ value: s._id, label: s.title })));
      } catch { setSections([]); }
    }
  };

  const validate = () => {
    const e = {};
    if (!testTitleRef.current?.value?.trim())          e.testTitle          = "Test title is required";
    if (!selectedBatches.length)                       e.batchIds           = "At least one batch is required";
    if (!selectedSubjects.length)                      e.testSubject        = "Select at least one subject";
    if (!testStartFromRef.current?.value?.trim())      e.testStartFrom      = "Start from is required";
    if (!testStartToRef.current?.value?.trim())        e.testStartTo        = "Start to is required";
    if (!testEndTimeRef.current?.value?.trim())        e.testEndTime        = "End time is required";
    if (!testDurationRef.current?.value?.trim())       e.testDuration       = "Duration is required";
    if (!isAnalysisRequiredRef.current?.value?.trim()) e.isAnalysisRequired = "Required";
    if (!testStatusRef.current?.value?.trim())         e.testStatus         = "Status is required";
    if (!testTypeRef.current?.value?.trim())           e.testType           = "Type is required";
    if (!requiredTimerRef.current?.value?.trim())      e.requiredTimer      = "Required";
    if (!formData.testDescription?.replace(/<[^>]*>/g, "").trim()) e.testDescription  = "Description is required";
    if (!formData.testInstructions?.replace(/<[^>]*>/g, "").trim()) e.testInstructions = "Instructions are required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    try {
      let payload = {
        testInstructions: formData.testInstructions,
        testDescription:  formData.testDescription,
        testTitle:       testTitleRef.current.value,
        batchIds:        selectedBatches.map(b => b.value),
        testSubject:     selectedSubjects.map(x => x.value),
        testModule:      selectedModules.map(x => x.value),
        testTopic:       formData.testTopic || null,
        testStartFrom:   fromDateTimeLocal(testStartFromRef.current.value),
        testStartTo:     fromDateTimeLocal(testStartToRef.current.value),
        testEndTime:     fromDateTimeLocal(testEndTimeRef.current.value),
        testDuration:    testDurationRef.current.value,
        testSection:     formData.testSection || null,
        isAnalysisRequired: isAnalysisRequiredRef.current.value,
        requiredTimer:   requiredTimerRef.current.value,
        protectiveTest:  protectiveTestRef.current?.checked || false,
        isFullScreen:    isFullScreenRef.current?.checked   || false,
        testType:        testTypeRef.current.value,
        testStatus:      testStatusRef.current.value,
      };
      if (showAnalysisFields) {
        payload = {
          ...payload,
          analysisStartFrom: fromDateTimeLocal(analysisStartFromRef.current.value),
          analysisStartTo:   fromDateTimeLocal(analysisStartToRef.current.value),
          analysisEndTime:   fromDateTimeLocal(analysisEndTimeRef.current.value),
          analysisDuration:  analysisDurationRef.current.value,
        };
      }
      await updateTestsApi(testId, payload);
      sessionStorage.setItem("ToastMessage", "Test updated successfully!");
      navigate("/tests");
    } catch (err) {
      setErrorMessage(err.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteTestById(testId);
      sessionStorage.setItem("ToastMessage", "Test deleted successfully!");
      navigate("/tests");
    } catch (error) {
      console.error("Error deleting test:", error);
    }
    setShowDeletePopup(false);
  };

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Loading…</p>
        </div>
      )}

      {showDeletePopup && (
        <div className="uf-delete-overlay">
          <div className="uf-delete-modal">
            <p className="uf-delete-msg">Are you sure you want to delete this test?</p>
            <div className="uf-delete-actions">
              <button className="uf-cancel-btn" onClick={() => setShowDeletePopup(false)}>Cancel</button>
              <button className="uf-confirm-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon sky">📋</div>
          <div className="af-header-text" style={{ flex: 1 }}>
            <h2 className="af-title">Update Test</h2>
            <p className="af-subtitle">Edit test structure, timing, and settings</p>
          </div>
          <button type="button" className="uf-delete-trigger" onClick={() => setShowDeletePopup(true)}>
            Delete
          </button>
        </div>

        {errorMessage && <div className="af-alert af-alert-error" style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            <div className="af-section">
              <p className="af-section-title">Test Information</p>
              <Field label="Test Title" required error={errors.testTitle}>
                <input type="text" name="testTitle" ref={testTitleRef}
                  value={formData.testTitle}
                  onChange={e => setFormData(p => ({...p, testTitle: e.target.value}))}
                  placeholder="e.g. React Mid-Term Test"
                  className={errors.testTitle ? "error-input" : ""} />
              </Field>
              <div className="af-row">
                <Field label="Batches" required error={errors.batchIds}>
                  <MultiSelectDropdown
                    options={batches}
                    value={selectedBatches}
                    onChange={setSelectedBatches}
                    placeholder="Select one or more batches…"
                    showCheckboxes
                  />
                  <span className="af-hint">
                    The test is offered to every batch selected. The first is its primary batch.
                  </span>
                </Field>
                <SelectField label="Test Type" required error={errors.testType}
                  name="testType" refProp={testTypeRef} value={formData.testType}
                  options={["Practice","Assignment","Daily-test","Weekly-test","Monthly-test"]}
                  placeholder="Select type"
                  onChange={e => setFormData(p => ({...p, testType: e.target.value}))} />
              </div>
              <Field label="Duration" required error={errors.testDuration}>
                <input type="text" name="testDuration" ref={testDurationRef}
                  value={formData.testDuration}
                  onChange={e => setFormData(p => ({...p, testDuration: e.target.value}))}
                  placeholder="e.g. 60 minutes"
                  className={errors.testDuration ? "error-input" : ""} />
              </Field>
            </div>

            <div className="af-section">
              <p className="af-section-title">Subject Classification</p>
              <div className="af-row">
                <Field label="Subject" required error={errors.testSubject}>
                  <MultiSelectDropdown
                    options={subjects}
                    value={selectedSubjects}
                    onChange={handleSubjectsChange}
                    placeholder={subjects.length === 0 ? "Loading…" : "Select one or more subjects"}
                    showCheckboxes
                  />
                </Field>
                <Field label="Module" error={errors.testModule}>
                  <MultiSelectDropdown
                    options={modules}
                    value={selectedModules}
                    onChange={handleModulesChange}
                    placeholder={!selectedSubjects.length ? "Select subject first" : modules.length === 0 ? "No modules" : "Optional — select modules"}
                    isDisabled={!selectedSubjects.length}
                    showCheckboxes
                  />
                </Field>
              </div>
              <div className="af-row">
                <SelectField label="Topic" error={errors.testTopic}
                  name="testTopic" refProp={testTopicRef} value={formData.testTopic}
                  options={topics}
                  placeholder={!selectedModules.length ? "Select module first" : topics.length === 0 ? "No topics" : "Optional — select topic"}
                  disabled={!selectedModules.length}
                  onChange={e => handleTopicChange(e.target.value)} />
                <SelectField label="Section" error={errors.testSection}
                  name="testSection" refProp={testSectionRef} value={formData.testSection}
                  options={sections}
                  placeholder={!formData.testTopic ? "Select topic first" : sections.length === 0 ? "No sections" : "Optional — select section"}
                  disabled={!formData.testTopic}
                  onChange={e => setFormData(p => ({...p, testSection: e.target.value}))} />
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Test Window</p>
              <div className="af-row">
                <Field label="Start Window — From" required error={errors.testStartFrom}>
                  <input type="datetime-local" name="testStartFrom" ref={testStartFromRef}
                    value={formData.testStartFrom}
                    onChange={e => setFormData(p => ({...p, testStartFrom: e.target.value}))}
                    className={errors.testStartFrom ? "error-input" : ""} />
                </Field>
                <Field label="Start Window — To" required error={errors.testStartTo}>
                  <input type="datetime-local" name="testStartTo" ref={testStartToRef}
                    value={formData.testStartTo}
                    onChange={e => setFormData(p => ({...p, testStartTo: e.target.value}))}
                    className={errors.testStartTo ? "error-input" : ""} />
                </Field>
              </div>
              <Field label="Test End Time" required error={errors.testEndTime}>
                <input type="datetime-local" name="testEndTime" ref={testEndTimeRef}
                  value={formData.testEndTime}
                  onChange={e => setFormData(p => ({...p, testEndTime: e.target.value}))}
                  className={errors.testEndTime ? "error-input" : ""} />
              </Field>
            </div>

            <div className="af-section">
              <p className="af-section-title">Analysis Settings</p>
              <SelectField label="Is Analysis Required?" required error={errors.isAnalysisRequired}
                name="isAnalysisRequired" refProp={isAnalysisRequiredRef}
                value={formData.isAnalysisRequired}
                options={["true","false"]} placeholder="Select…"
                onChange={e => {
                  setFormData(p => ({...p, isAnalysisRequired: e.target.value}));
                  setShowAnalysisFields(e.target.value === "true");
                }} />

              {showAnalysisFields && (
                <>
                  <div className="af-row">
                    <Field label="Analysis Start — From" error={errors.analysisStartFrom}>
                      <input type="datetime-local" name="analysisStartFrom" ref={analysisStartFromRef}
                        value={formData.analysisStartFrom}
                        onChange={e => setFormData(p => ({...p, analysisStartFrom: e.target.value}))} />
                    </Field>
                    <Field label="Analysis Start — To" error={errors.analysisStartTo}>
                      <input type="datetime-local" name="analysisStartTo" ref={analysisStartToRef}
                        value={formData.analysisStartTo}
                        onChange={e => setFormData(p => ({...p, analysisStartTo: e.target.value}))} />
                    </Field>
                  </div>
                  <div className="af-row">
                    <Field label="Analysis End Time" error={errors.analysisEndTime}>
                      <input type="datetime-local" name="analysisEndTime" ref={analysisEndTimeRef}
                        value={formData.analysisEndTime}
                        onChange={e => setFormData(p => ({...p, analysisEndTime: e.target.value}))} />
                    </Field>
                    <Field label="Analysis Duration" error={errors.analysisDuration}>
                      <input type="text" name="analysisDuration" ref={analysisDurationRef}
                        value={formData.analysisDuration}
                        onChange={e => setFormData(p => ({...p, analysisDuration: e.target.value}))}
                        placeholder="e.g. 30 minutes" />
                    </Field>
                  </div>
                </>
              )}
            </div>

            <div className="af-section">
              <p className="af-section-title">Test Settings</p>
              <div className="af-row">
                <SelectField label="Status" required error={errors.testStatus}
                  name="testStatus" refProp={testStatusRef} value={formData.testStatus}
                  options={["Drafted","Live","Completed"]} placeholder="Select status"
                  onChange={e => setFormData(p => ({...p, testStatus: e.target.value}))} />
                <SelectField label="Required Timer" required error={errors.requiredTimer}
                  name="requiredTimer" refProp={requiredTimerRef} value={formData.requiredTimer}
                  options={["true","false"]} placeholder="Select…"
                  onChange={e => setFormData(p => ({...p, requiredTimer: e.target.value}))} />
              </div>

              <div className="af-toggles">
                <div className="af-toggle-row">
                  <span className="af-toggle-label">Protective test (prevent tab switch)</span>
                  <label className="af-toggle">
                    <input type="checkbox" ref={protectiveTestRef}
                      checked={formData.protectiveTest}
                      onChange={() => setFormData(p => ({...p, protectiveTest: !p.protectiveTest}))} />
                    <span className="af-toggle-track" />
                  </label>
                </div>
                <div className="af-toggle-row">
                  <span className="af-toggle-label">Open test in full screen</span>
                  <label className="af-toggle">
                    <input type="checkbox" ref={isFullScreenRef}
                      checked={formData.isFullScreen}
                      onChange={() => setFormData(p => ({...p, isFullScreen: !p.isFullScreen}))} />
                    <span className="af-toggle-track" />
                  </label>
                </div>
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Content</p>
              <div className="af-field">
                <label className="af-label">Test Description<span className="af-required">*</span></label>
                <div className="af-editor-wrap">
                  <RichTextEditor
                    value={formData.testDescription}
                    onBlur={v => setFormData(p => ({...p, testDescription: v}))}
                    toolbar="full"
                    height={520}
                    placeholder="Describe what this test covers…"
                  />
                </div>
                {errors.testDescription && <span className="af-error">{errors.testDescription}</span>}
              </div>

              <div className="af-field">
                <label className="af-label">Test Instructions<span className="af-required">*</span></label>
                <div className="af-editor-wrap">
                  <RichTextEditor
                    value={formData.testInstructions}
                    onBlur={v => setFormData(p => ({...p, testInstructions: v}))}
                    toolbar="full"
                    height={520}
                    placeholder="Instructions for students…"
                  />
                </div>
                {errors.testInstructions && <span className="af-error">{errors.testInstructions}</span>}
              </div>
            </div>
          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Saving…" : "Update Test"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateTest;
